import {
  AssertionError,
  LiquidError,
  ParseError,
  RenderError,
  Token,
  TokenizationError,
  UndefinedVariableError,
} from 'liquidjs';

type Props = {
  template: string;
  variables: Record<string, unknown>;
};
export class WpRendererError extends Error {
  template: string;
  variables: Record<string, unknown>;
  constructor(message: string, { template, variables }: Props) {
    super(message);
    this.template = template;
    this.variables = variables;
  }

  getDatabaseErrorMessage() {
    return {
      message: this.message,
      template: this.template,
      variables: this.variables,
    };
  }
}

class WpLiquidRenderError extends WpRendererError {}
class WpLiquidTokenizationError extends WpRendererError {}
class WpLiquidParseError extends WpRendererError {}
class WpLiquidUndefinedVariableError extends WpRendererError {}
class WpLiquidAssertionError extends WpRendererError {}
class WpUnknownLiquidError extends WpRendererError {}

export default function normalizeRenderingError(e: unknown, props: Props): WpRendererError {
  const error = extractOriginalError(e);

  if (error instanceof RenderError) {
    return new WpLiquidRenderError('There was an error during rendering', props);
  }
  if (error instanceof TokenizationError) {
    const message = error.message.split(/, line:\d+, col:\d+\s*$/)[0];
    return new WpLiquidTokenizationError(message, props);
  }
  if (error instanceof ParseError) {
    return new WpLiquidParseError('There was an error parsing the template', props);
  }
  if (error instanceof UndefinedVariableError) {
    const token = extractToken(error);
    if (token) {
      return new WpLiquidUndefinedVariableError(`"${token.getText()}" is undefined`, props);
    }
    return new WpLiquidUndefinedVariableError('There is an undefined variable', props);
  }
  if (error instanceof AssertionError) {
    return new WpLiquidAssertionError('There was an assertion error', props);
  }
  return new WpUnknownLiquidError('An unknown error occurred', props);
}

// LiquidErrors sometimes get wrapped in a RenderError which makes it hard
// to get the original error message.
function extractOriginalError(error: unknown): unknown {
  if (error instanceof RenderError) {
    // The RenderError has a private reference to the originalError inside
    const { originalError } = error as unknown as { originalError?: Error };
    if (originalError instanceof LiquidError) {
      return originalError;
    }
  }
  return error;
}

// token is a private variable
function extractToken(error: UndefinedVariableError | TokenizationError): Token | null {
  const { token } = error as unknown as { token?: Token };
  return token || null;
}
